import { useState, type FormEvent } from 'react';
import Reveal from '@/components/feature/Reveal';
import SectionTitle from './SectionTitle';

const FORM_URL = 'https://readdy.ai/api/form/da2lk63d3r93mn1k3bjg';

export default function Contact() {
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('idle');
    setMessage('');

    const form = e.currentTarget;
    const data = new FormData(form);

    // 蜜罐校验：命中则伪装成功，不提交
    const honeypot = String(data.get('phone_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      setMessage('感谢你的反馈，我们会尽快处理。');
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    Array.from(data.entries()).forEach(([key, value]) => {
      if (key === 'phone_alt') return;
      if (typeof value === 'string' && value.trim()) payload.append(key, value);
    });

    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const meta = (parsed?.meta ?? {}) as Record<string, unknown>;
      const serverMsg = String(meta?.message || parsed?.message || '') || responseText;

      const isSpam = /spam|form data is spam/i.test(serverMsg);
      const ok = res.ok && parsed?.code === 'OK' && !isSpam;

      if (ok) {
        setStatus('success');
        setMessage(serverMsg || '反馈已收到，感谢你的支持！');
        form.reset();
      } else {
        setStatus('error');
        setMessage(serverMsg || '提交失败，请稍后再试。');
      }
    } catch {
      setStatus('error');
      setMessage('网络异常，提交失败，请稍后再试。');
    }
  };

  return (
    <section id="contact" className="relative overflow-hidden bg-primary-700 py-16 md:py-24">
      <div className="pointer-events-none absolute right-[-4rem] top-[-4rem] h-72 w-72 rounded-full bg-primary-500/40 blur-3xl"></div>
      <div className="pointer-events-none absolute bottom-[-4rem] left-[-4rem] h-72 w-72 rounded-full bg-secondary-500/30 blur-3xl"></div>

      <div className="relative mx-auto max-w-7xl px-4 md:px-6">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 md:gap-14">
          {/* 左：文案 */}
          <Reveal>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-500/50 px-3.5 py-1 text-xs font-semibold text-background-50">
              <i className="ri-chat-3-fill"></i>
              联系我们
            </span>
            <h2 className="mt-4 font-heading text-2xl leading-snug text-background-50 md:text-4xl">
              有好番想推荐？
              <br />
              有入口找不到？
            </h2>
            <p className="mt-5 max-w-md text-sm leading-7 text-background-100/90">
              你的每一个反馈，都能让卡通动漫第一页变得更好用。无论是想看的作品找不到入口、发现更新有误，还是单纯想推荐一部好番，都欢迎告诉我们。收藏本站，追更更省心。
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <div className="flex items-center gap-2 rounded-full bg-primary-500/40 px-4 py-2 text-xs text-background-50">
                <i className="ri-mail-fill"></i>
                hi@kttongman-diyiye.com
              </div>
              <div className="flex items-center gap-2 rounded-full bg-primary-500/40 px-4 py-2 text-xs text-background-50">
                <i className="ri-time-line"></i>
                反馈响应：24 小时内
              </div>
            </div>
          </Reveal>

          {/* 右：表单卡片 */}
          <Reveal delay={120}>
            <form data-readdy-form onSubmit={handleSubmit} className="rounded-3xl border border-background-50/15 bg-background-50 p-6 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.4)] md:p-8">
              <h3 className="font-heading text-lg text-foreground-950">给我们留言</h3>
              <p className="mt-1 text-xs text-foreground-500">推荐作品、反馈问题或收录建议，都欢迎告诉我们。</p>

              <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="flex flex-col">
                  <label htmlFor="name" className="mb-1.5 text-xs font-semibold text-foreground-700">你的称呼</label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="怎么称呼你"
                    className="rounded-lg border border-background-200 bg-background-100/60 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:bg-background-50"
                  />
                </div>
                <div className="flex flex-col">
                  <label htmlFor="email" className="mb-1.5 text-xs font-semibold text-foreground-700">你的邮箱</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="name@example.com"
                    className="rounded-lg border border-background-200 bg-background-100/60 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:bg-background-50"
                  />
                </div>
              </div>

              <div className="mt-4 flex flex-col">
                <label htmlFor="type" className="mb-1.5 text-xs font-semibold text-foreground-700">反馈类型</label>
                <select
                  id="type"
                  name="type"
                  className="rounded-lg border border-background-200 bg-background-100/60 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:bg-background-50"
                >
                  <option value="推荐作品">推荐作品</option>
                  <option value="反馈问题">反馈问题</option>
                  <option value="收录建议">收录建议</option>
                  <option value="其他">其他</option>
                </select>
              </div>

              <div className="mt-4 flex flex-col">
                <label htmlFor="message" className="mb-1.5 text-xs font-semibold text-foreground-700">留言内容</label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  maxLength={500}
                  placeholder="想说的话（最多 500 字）"
                  className="resize-none rounded-lg border border-background-200 bg-background-100/60 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:bg-background-50"
                />
              </div>

              {/* 蜜罐防垃圾字段 */}
              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="hp-field"
              />

              <button
                type="submit"
                className="mt-5 w-full rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
              >
                提交反馈
              </button>

              {status === 'success' && (
                <p className="mt-4 flex items-center gap-2 rounded-lg bg-accent-100 px-3.5 py-2.5 text-xs font-semibold text-accent-800">
                  <i className="ri-checkbox-circle-fill text-base"></i>
                  {message}
                </p>
              )}
              {status === 'error' && (
                <p className="mt-4 flex items-center gap-2 rounded-lg bg-primary-100 px-3.5 py-2.5 text-xs font-semibold text-primary-800">
                  <i className="ri-error-warning-fill text-base"></i>
                  {message}
                </p>
              )}
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}